/**
 * ClipCreator Component
 * Allows users to create 10-30 second clips from videos
 */
import { useState, useEffect } from 'react';
import { Scissors, Clock, Play, Pause, Download, Share2, X, Check } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const ClipCreator = ({ 
  recordingId, 
  recordingTitle,
  duration,
  currentTime,
  onSeek,
  isOpen,
  onClose 
}) => {
  const [startTime, setStartTime] = useState(0);
  const [endTime, setEndTime] = useState(30);
  const [clipTitle, setClipTitle] = useState('');
  const [creating, setCreating] = useState(false);
  const [createdClip, setCreatedClip] = useState(null);
  const [error, setError] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('userToken');
    setIsLoggedIn(!!token);
  }, []);

  useEffect(() => {
    if (isOpen && currentTime !== undefined) {
      // Set start time to current position
      const start = Math.floor(currentTime);
      setStartTime(start);
      // Set end time to start + 30 (or duration if shorter)
      setEndTime(Math.min(start + 30, duration || start + 30));
    }
  }, [isOpen, currentTime, duration]);

  const clipDuration = endTime - startTime;
  const isValidDuration = clipDuration >= 10 && clipDuration <= 30;

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSetStart = () => {
    if (currentTime !== undefined) {
      const newStart = Math.floor(currentTime);
      setStartTime(newStart);
      if (endTime <= newStart) {
        setEndTime(Math.min(newStart + 30, duration || newStart + 30));
      }
    }
  };

  const handleSetEnd = () => {
    if (currentTime !== undefined) {
      const newEnd = Math.floor(currentTime);
      if (newEnd > startTime) {
        setEndTime(newEnd);
      }
    }
  };

  const handlePreview = () => {
    if (onSeek) {
      onSeek(startTime);
    }
  };

  const handleCreateClip = async () => {
    if (!isLoggedIn) {
      setError('Please sign in to create clips');
      return;
    }

    if (!isValidDuration) {
      setError('Clip must be between 10-30 seconds');
      return;
    }

    setCreating(true);
    setError('');

    try {
      const token = localStorage.getItem('userToken');
      const response = await axios.post(`${API}/clips/create`, {
        recording_id: recordingId,
        start_time: startTime,
        end_time: endTime,
        title: clipTitle || undefined
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setCreatedClip(response.data.clip);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create clip');
    } finally {
      setCreating(false);
    }
  };

  const handleCopyLink = () => {
    if (createdClip?.clip_url) {
      navigator.clipboard.writeText(createdClip.clip_url);
    }
  };

  const handleShare = async () => {
    if (createdClip && navigator.share) {
      try {
        await navigator.share({
          title: createdClip.title,
          text: `Check out this clip from ${createdClip.recording_title}`,
          url: createdClip.clip_url
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="clip-creator-modal">
      <div className="bg-zinc-900 border border-white/10 w-full max-w-lg">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Scissors className="w-5 h-5 text-orange-500" />
            <h2 className="font-heading text-lg font-bold uppercase text-white">Create Clip</h2>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/10 transition-colors">
            <X className="w-5 h-5 text-zinc-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {!isLoggedIn && (
            <div className="bg-orange-500/10 border border-orange-500/30 p-3 text-sm text-orange-500">
              Please sign in to create clips
            </div>
          )}

          {createdClip ? (
            // Clip Created Success
            <div className="space-y-4">
              <div className="bg-green-500/10 border border-green-500/30 p-4 text-center">
                <Check className="w-10 h-10 text-green-500 mx-auto mb-2" />
                <p className="text-green-500 font-bold">Clip Created!</p>
              </div>

              <div className="bg-zinc-800 p-3">
                <p className="text-xs text-zinc-500 mb-1">Clip Title</p>
                <p className="text-white font-medium">{createdClip.title}</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="bg-zinc-800 p-3">
                  <p className="text-xs text-zinc-500 mb-1">Duration</p>
                  <p className="text-white font-medium">{formatTime(createdClip.duration)}</p>
                </div>
                <div className="bg-zinc-800 p-3">
                  <p className="text-xs text-zinc-500 mb-1">Source</p>
                  <p className="text-white font-medium capitalize">{createdClip.source_type}</p>
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={handleCopyLink}
                  className="flex-1 bg-zinc-800 text-white hover:bg-zinc-700 font-secondary text-xs h-auto py-2"
                  data-testid="copy-clip-link"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Copy Link
                </Button>
                <Button
                  onClick={handleShare}
                  className="flex-1 bg-orange-500 text-white hover:bg-orange-600 font-secondary text-xs h-auto py-2"
                  data-testid="share-clip"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Share
                </Button>
              </div>

              <Button
                onClick={() => {
                  setCreatedClip(null);
                  setClipTitle('');
                }}
                className="w-full bg-zinc-700 text-white hover:bg-zinc-600 font-secondary text-xs h-auto py-2"
              >
                Create Another Clip
              </Button>
            </div>
          ) : (
            // Clip Creation Form
            <>
              {/* Source Info */}
              <div className="bg-zinc-800 p-3">
                <p className="text-xs text-zinc-500 mb-1">Creating clip from</p>
                <p className="text-white font-medium truncate">{recordingTitle}</p>
              </div>

              {/* Time Selection */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-zinc-500 mb-2">Start Time</label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      value={startTime}
                      onChange={(e) => setStartTime(Math.max(0, parseInt(e.target.value) || 0))}
                      className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm"
                      min={0}
                      max={duration || 9999}
                      data-testid="clip-start-time"
                    />
                    <Button
                      onClick={handleSetStart}
                      className="bg-zinc-700 text-white hover:bg-zinc-600 px-3 py-2 h-auto text-xs"
                      title="Set to current position"
                    >
                      Now
                    </Button>
                  </div>
                  <p className="text-[10px] text-zinc-600 mt-1">{formatTime(startTime)}</p>
                </div>
                <div>
                  <label className="block text-xs text-zinc-500 mb-2">End Time</label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      value={endTime}
                      onChange={(e) => setEndTime(Math.max(startTime + 1, parseInt(e.target.value) || 0))}
                      className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm"
                      min={startTime + 10}
                      max={duration || 9999}
                      data-testid="clip-end-time"
                    />
                    <Button
                      onClick={handleSetEnd}
                      className="bg-zinc-700 text-white hover:bg-zinc-600 px-3 py-2 h-auto text-xs"
                      title="Set to current position"
                    >
                      Now
                    </Button>
                  </div>
                  <p className="text-[10px] text-zinc-600 mt-1">{formatTime(endTime)}</p>
                </div>
              </div>

              {/* Duration Display */}
              <div className={`p-3 flex items-center justify-between ${
                isValidDuration ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'
              }`}>
                <div className="flex items-center gap-2">
                  <Clock className={`w-4 h-4 ${isValidDuration ? 'text-green-500' : 'text-red-500'}`} />
                  <span className={`text-sm font-bold ${isValidDuration ? 'text-green-500' : 'text-red-500'}`}>
                    {formatTime(clipDuration)}
                  </span>
                </div>
                <span className={`text-xs ${isValidDuration ? 'text-green-500' : 'text-red-500'}`}>
                  {clipDuration < 10 ? 'Too short (min 10s)' : 
                   clipDuration > 30 ? 'Too long (max 30s)' : 
                   'Valid duration'}
                </span>
              </div>

              {/* Preview Button */}
              <Button
                onClick={handlePreview}
                className="w-full bg-zinc-800 text-white hover:bg-zinc-700 font-secondary text-xs h-auto py-2"
                data-testid="preview-clip"
              >
                <Play className="w-4 h-4 mr-2" />
                Preview from Start Time
              </Button>

              {/* Clip Title */}
              <div>
                <label className="block text-xs text-zinc-500 mb-2">Clip Title (optional)</label>
                <input
                  type="text"
                  value={clipTitle}
                  onChange={(e) => setClipTitle(e.target.value)}
                  placeholder={`Clip from ${recordingTitle}`}
                  className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm"
                  maxLength={100}
                  data-testid="clip-title-input"
                />
              </div>

              {/* Error Display */}
              {error && (
                <div className="bg-red-500/10 border border-red-500/30 p-3 text-sm text-red-500">
                  {error}
                </div>
              )}

              {/* Create Button */}
              <Button
                onClick={handleCreateClip}
                disabled={!isValidDuration || creating || !isLoggedIn}
                className="w-full bg-orange-500 text-white hover:bg-orange-600 disabled:bg-zinc-700 disabled:text-zinc-500 font-secondary text-sm h-auto py-3"
                data-testid="create-clip-btn"
              >
                {creating ? (
                  <>Creating Clip...</>
                ) : (
                  <>
                    <Scissors className="w-4 h-4 mr-2" />
                    Create {formatTime(clipDuration)} Clip
                  </>
                )}
              </Button>

              <p className="text-[10px] text-zinc-600 text-center">
                Clips are 10-30 seconds and can be shared with a direct link
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClipCreator;
